<main class="spike-shell">
  <div class="page-header__row">
    <h1>{{ title() }}</h1>
    <span class="badge">Beta</span>
  </div>
  <p>
    Spike step 5 — per-framework empirical measurements (heap, wall-clock,
    message counts) against EBA RF 4.2.
  </p>

  <section>
    <h2>Web worker</h2>
    <app-job-status-line [phase]="workerStatus()" />
    @if (lastResponse()) {
      <pre>{{ lastResponse() }}</pre>
    }
  </section>

  <section>
    <h2>Pyodide runtime</h2>
    <dl>
      <dt>Status</dt>
      <dd><app-job-status-line mode="value" [phase]="pyodideStatus()" /></dd>

      @if (pyodideVersion()) {
        <dt>Version</dt>
        <dd>{{ pyodideVersion() }}</dd>
      }

      @if (pyodideLoadMs() !== null) {
        <dt>Load time</dt>
        <dd>{{ pyodideLoadMs() }} ms</dd>
      }

      @if (pyodideError()) {
        <dt>Error</dt>
        <dd><code>{{ pyodideError() }}</code></dd>
      }
    </dl>
  </section>

  <section>
    <h2>Arelle</h2>
    <dl>
      <dt>Status</dt>
      <dd><app-job-status-line mode="value" [phase]="arelleStatus()" /></dd>

      @if (arelleVersion()) {
        <dt>Arelle version</dt>
        <dd>{{ arelleVersion() }}</dd>
        <dt>Python version</dt>
        <dd>{{ pythonVersion() }}</dd>
        <dt>loadPackage (micropip wheel)</dt>
        <dd>{{ arellePackagesMs() }} ms</dd>
        <dt>micropip install (22 wheels)</dt>
        <dd>{{ arelleMicropipMs() }} ms</dd>
        <dt>from arelle import Cntlr</dt>
        <dd>{{ arelleImportMs() }} ms</dd>
        <dt>Arelle subtotal</dt>
        <dd>{{ arelleTotalMs() }} ms</dd>
      }

      @if (arelleError()) {
        <dt>Error ({{ arelleErrorPhase() }})</dt>
        <dd><code>{{ arelleError() }}</code></dd>
      }
    </dl>
  </section>

  <section>
    <h2>Taxonomy (EBA RF 4.2 hotfix)</h2>
    <dl>
      <dt>Status</dt>
      <dd><app-job-status-line mode="value" [phase]="taxonomyStatus()" /></dd>

      @if (taxonomyPackages().length > 0) {
        <dt>Packages registered</dt>
        <dd>{{ taxonomyPackages().length }}</dd>
        <dt>Total entry points</dt>
        <dd>{{ taxonomyTotalEntryPoints() }}</dd>
        <dt>Total URL remappings</dt>
        <dd>{{ taxonomyTotalRemappings() }}</dd>
        <dt>Total bytes (3 zips)</dt>
        <dd>{{ taxonomyTotalBytes() }} bytes</dd>
        <dt>WebCache workOffline</dt>
        <dd><strong>{{ taxonomyWorkOffline() }}</strong></dd>
        <dt>fetch (3 × parallel)</dt>
        <dd>{{ taxonomyFetchMs() }} ms</dd>
        <dt>write to VFS</dt>
        <dd>{{ taxonomyWriteMs() }} ms</dd>
        <dt>PackageManager.addPackage × 3 + rebuildRemappings</dt>
        <dd>{{ taxonomyRegisterMs() }} ms</dd>
        <dt>Taxonomy mount subtotal</dt>
        <dd>{{ taxonomyTotalMs() }} ms</dd>
      }

      @if (taxonomyError()) {
        <dt>Error ({{ taxonomyErrorPhase() }})</dt>
        <dd><code>{{ taxonomyError() }}</code></dd>
      }
    </dl>

    @if (taxonomyPackages().length > 0) {
      <table>
        <thead>
          <tr>
            <th>Key</th>
            <th>Name</th>
            <th>Version</th>
            <th>Identifier</th>
            <th>Entry points</th>
            <th>Remappings</th>
            <th>Size</th>
          </tr>
        </thead>
        <tbody>
          @for (pkg of taxonomyPackages(); track pkg.key) {
            <tr>
              <td><code>{{ pkg.key }}</code></td>
              <td>{{ pkg.name }}</td>
              <td>{{ pkg.version }}</td>
              <td><code>{{ pkg.identifier }}</code></td>
              <td>{{ pkg.entryPointCount }}</td>
              <td>{{ pkg.remappingCount }}</td>
              <td>{{ pkg.bytes }} B</td>
            </tr>
          }
        </tbody>
      </table>
    }
  </section>

  @if (coldStartTotalMs() !== null) {
    <section>
      <h2>Spike criterion 1 — total cold start</h2>
      <p>
        Pyodide + Arelle: <strong>{{ coldStartTotalMs() }} ms</strong>
        @if ((coldStartTotalMs() ?? 0) > 30000) {
          <span> &mdash; <em>exceeds threshold (30 s)</em></span>
        } @else {
          <span> &mdash; <em>within threshold (30 s)</em></span>
        }
      </p>
    </section>
  }

  <section>
    <h2>Validate a filing</h2>
    <p>
      <app-job-status-line mode="inline" [phase]="validateStatus()" />
      @if (validateFilename()) {
        — file: <code>{{ validateFilename() }}</code>
      }
    </p>

    @for (group of groupedSamples(); track group.family) {
      <h3>{{ group.family }} ({{ group.samples.length }})</h3>
      <table>
        <thead>
          <tr>
            <th>Label</th>
            <th>Period</th>
            <th>Size</th>
            <th>Note</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          @for (s of group.samples; track s.url) {
            <tr>
              <td>{{ s.label }}</td>
              <td>{{ s.period }}</td>
              <td>{{ formatBytesMB(s.sizeBytes) }}</td>
              <td>
                @if (s.note) { <em>{{ s.note }}</em> }
              </td>
              <td>
                <button type="button" [disabled]="!canValidate()" (click)="validateSample(s)">
                  Validate
                </button>
                <button type="button" [disabled]="!canLightLoad()" (click)="lightLoadSample(s)">
                  Light load
                </button>
                <button type="button" [disabled]="!canTrendingTest()" (click)="trendingTestSample(s)">
                  Trending (×2)
                </button>
                <button type="button" [disabled]="!canReports()" (click)="exportReportsSample(s)">
                  Reports
                </button>
              </td>
            </tr>
          }
        </tbody>
      </table>
    }

    @if (validateStatus() === 'fetch' ||
         validateStatus() === 'write' ||
         validateStatus() === 'load' ||
         validateStatus() === 'validate') {
      <p>
        <app-job-status-line mode="inline" prefix="Phase" [phase]="validateStatus()" />
        — elapsed: {{ validateElapsedMs() }} ms
        @if (validateUsedHeapBytes() !== null) {
          — JS heap: {{ validateUsedHeapBytes() }} B
        }
      </p>
      <p>
        <em>
          Note: while a phase is running inside Python, the worker is busy and
          heartbeats pause until the phase completes.
        </em>
      </p>
    }

    @if (validateError()) {
      <dl>
        <dt>Error ({{ validateErrorPhase() }})</dt>
        <dd><code>{{ validateError() }}</code></dd>
      </dl>
    }

    @if (validateStatus() === 'ready' && validateCounts() !== null) {
      <dl>
        <dt>Total messages</dt>
        <dd>{{ validateCounts()?.total }}</dd>
        <dt>Errors / Warnings / Info</dt>
        <dd>
          {{ validateCounts()?.error }} /
          {{ validateCounts()?.warning }} /
          {{ validateCounts()?.info }}
        </dd>
        <dt>Facts in instance</dt>
        <dd>{{ validateFactsCount() }}</dd>
        <dt>Truncated at</dt>
        <dd>
          @if (validateTruncatedAt() !== null) {
            first {{ validateTruncatedAt() }} messages shown (of {{ validateCounts()?.total }})
          } @else {
            no — all {{ validateCounts()?.total }} messages shown
          }
        </dd>
        <dt>Bytes (instance file)</dt>
        <dd>{{ validateBytes() }} B</dd>
        <dt>fetch</dt>
        <dd>{{ validateFetchMs() }} ms</dd>
        <dt>write to VFS</dt>
        <dd>{{ validateWriteMs() }} ms</dd>
        <dt>load DTS</dt>
        <dd>{{ validateLoadMs() }} ms</dd>
        <dt>validate</dt>
        <dd>{{ validateValidateMs() }} ms</dd>
        <dt>Validation total</dt>
        <dd>
          <strong>{{ validateTotalMs() }} ms</strong>
          @if (criterion2WithinThreshold() === true) {
            — <em>within threshold (2 min)</em>
          } @else if (criterion2WithinThreshold() === false) {
            — <em>exceeds threshold (2 min)</em>
          }
        </dd>
        <dt>Peak JS heap (worker)</dt>
        <dd>{{ validatePeakHeapBytes() }} B</dd>
      </dl>

      @if (validateTopCodes().length > 0) {
        <h3>Top message codes</h3>
        <table>
          <thead>
            <tr>
              <th>Code</th>
              <th>Level</th>
              <th>Count</th>
            </tr>
          </thead>
          <tbody>
            @for (tc of validateTopCodes(); track tc.code + '|' + tc.level) {
              <tr>
                <td><code>{{ tc.code }}</code></td>
                <td>{{ tc.level }}</td>
                <td>{{ tc.count }}</td>
              </tr>
            }
          </tbody>
        </table>
      }

      <h3>Diagnostics</h3>
      <dl>
        <dt>WASM heap before load</dt>
        <dd>{{ formatBytesMB(wasmHeapBeforeLoadBytes()) }}</dd>
        <dt>WASM heap after load</dt>
        <dd>
          {{ formatBytesMB(wasmHeapAfterLoadBytes()) }}
          (Δ load = {{ wasmHeapGrowthLoadMB() }})
        </dd>
        <dt>WASM heap after validate</dt>
        <dd>
          {{ formatBytesMB(wasmHeapAfterValidateBytes()) }}
          (Δ validate = {{ wasmHeapGrowthValidateMB() }})
        </dd>
        <dt>Validate.validate() raised</dt>
        <dd>
          @if (validateFailed()) {
            <strong>yes</strong> — diagnostics captured below
          } @else {
            no — validation completed without uncaught exception
          }
        </dd>
        @if (modelXbrlErrors().length > 0) {
          <dt>modelXbrl.errors</dt>
          <dd>
            {{ modelXbrlErrors().length }} error code(s) accumulated by Arelle
            during validation:
            <ul>
              @for (e of modelXbrlErrors(); track $index) {
                <li><code>{{ e }}</code></li>
              }
            </ul>
          </dd>
        }
      </dl>

      @if (validateException(); as exc) {
        <h3>Uncaught Python exception during Validate.validate()</h3>
        <dl>
          <dt>Type</dt>
          <dd><code>{{ exc.type }}</code></dd>
          <dt>Message</dt>
          <dd>{{ exc.message || '(empty)' }}</dd>
          <dt>Python traceback</dt>
          <dd><pre>{{ exc.traceback }}</pre></dd>
        </dl>
      }

      @if (validateProfileStats().length > 0) {
        <h3>
          Profile statistics
          @if (validateProfileTotalSec() !== null) {
            — Arelle-reported total: {{ validateProfileTotalSec() }} s
          }
        </h3>
        <p>
          Per-phase timings collected by Arelle's <code>collectProfileStats</code>.
          Sorted by descending wall-clock time. Memory column is Arelle's
          process-side estimate; <code>—</code> when not available.
        </p>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Phase</th>
              <th>Time (ms)</th>
              <th>Mem (kB)</th>
              <th>Share of validate</th>
            </tr>
          </thead>
          <tbody>
            @for (p of validateProfileStats(); track p.name; let i = $index) {
              <tr>
                <td>{{ i + 1 }}</td>
                <td><code>{{ p.name }}</code></td>
                <td>{{ formatMs(p.ms) }}</td>
                <td>{{ formatMem(p.memKB) }}</td>
                <td>{{ shareOfValidate(p.ms) }}</td>
              </tr>
            }
          </tbody>
        </table>
      }

      @if (validateMessages().length > 0) {
        <details [attr.open]="validateMessages().length <= 50 ? '' : null">
          <summary>
            All messages ({{ validateMessages().length }} of
            {{ validateCounts()?.total }} returned to UI)
          </summary>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Level</th>
                <th>Code</th>
                <th>Message</th>
                <th>Refs</th>
              </tr>
            </thead>
            <tbody>
              @for (m of validateMessages(); track $index) {
                <tr>
                  <td>{{ $index + 1 }}</td>
                  <td>{{ m.level }}</td>
                  <td><code>{{ m.code || '(no code)' }}</code></td>
                  <td>
                    @if (m.text) {
                      <pre>{{ m.text }}</pre>
                    } @else {
                      <em>(empty message text)</em>
                    }
                  </td>
                  <td>
                    @for (ref of m.refs; track $index) {
                      <div>
                        @if (ref.href) { <code>{{ ref.href }}</code> }
                        @if (ref.sourceLine !== undefined) {
                          : line {{ ref.sourceLine }}
                        }
                      </div>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </details>
      }
    }
  </section>

  <section>
    <h2>Light load — viewer/trending path (no Arelle, lxml only)</h2>
    <p>
      Parses the instance file directly with lxml, extracts facts / contexts /
      units, and looks up English labels from the EBA Dictionary + Reporting
      Frameworks zips. <strong>No DTS discovery, no validation, no formula
      evaluation.</strong> Intended for the Fase 1 viewer / trending code path
      on frameworks too large for full Arelle load.
    </p>
    <p>
      <app-job-status-line mode="inline" [phase]="lightLoadStatus()" />
      @if (lightLoadFilename()) {
        — file: <code>{{ lightLoadFilename() }}</code>
      }
    </p>

    @if (lightLoadStatus() === 'fetch' ||
         lightLoadStatus() === 'write' ||
         lightLoadStatus() === 'parse' ||
         lightLoadStatus() === 'labels') {
      <p>
        <app-job-status-line mode="inline" prefix="Phase" [phase]="lightLoadStatus()" />
        — elapsed: {{ lightLoadElapsedMs() }} ms
      </p>
    }

    @if (lightLoadError()) {
      <dl>
        <dt>Error ({{ lightLoadErrorPhase() }})</dt>
        <dd><code>{{ lightLoadError() }}</code></dd>
      </dl>
    }

    @if (lightLoadStatus() === 'ready' && lightLoadFactsCount() !== null) {
      <dl>
        <dt>Bytes (instance file)</dt>
        <dd>{{ formatBytesMB(lightLoadBytes()) }} ({{ lightLoadBytes() }} B)</dd>
        <dt>fetch</dt>
        <dd>{{ lightLoadFetchMs() }} ms</dd>
        <dt>write to VFS</dt>
        <dd>{{ lightLoadWriteMs() }} ms</dd>
        <dt>parse (lxml)</dt>
        <dd>{{ lightLoadParseMs() }} ms</dd>
        <dt>label index build</dt>
        <dd>
          @if (lightLoadLabelsBuilt()) {
            {{ lightLoadLabelBuildMs() }} ms (built this run)
          } @else {
            0 ms (reused cached index from previous light-load)
          }
        </dd>
        <dt>Light-load total</dt>
        <dd><strong>{{ lightLoadTotalMs() }} ms</strong></dd>

        <dt>Facts in instance</dt>
        <dd>{{ lightLoadFactsCount() }}</dd>
        <dt>Contexts in instance</dt>
        <dd>{{ lightLoadContextsCount() }}</dd>
        <dt>Units in instance</dt>
        <dd>{{ lightLoadUnitsCount() }}</dd>
        <dt>Unique concepts in instance</dt>
        <dd>{{ lightLoadUniqueConceptsCount() }}</dd>
        <dt>Label index size (EN, standard role)</dt>
        <dd>{{ lightLoadLabelIndexSize() }} concept→label entries</dd>
        <dt>Label coverage on unique concepts</dt>
        <dd>
          <strong>{{ lightLoadUniqueWithLabel() }}</strong> matched,
          {{ lightLoadUniqueWithoutLabel() }} missing
          @if (lightLoadUniqueConceptsCount() && lightLoadUniqueConceptsCount()! > 0) {
            ({{ ((lightLoadUniqueWithLabel() ?? 0) / lightLoadUniqueConceptsCount()! * 100).toFixed(1) }}%)
          }
        </dd>

        <dt>WASM heap before</dt>
        <dd>{{ formatBytesMB(lightLoadHeapBeforeBytes()) }}</dd>
        <dt>WASM heap after parse</dt>
        <dd>{{ formatBytesMB(lightLoadHeapAfterParseBytes()) }}</dd>
        <dt>WASM heap after labels</dt>
        <dd>{{ formatBytesMB(lightLoadHeapAfterLabelsBytes()) }}</dd>
      </dl>

      @if (lightLoadSampleFacts().length > 0) {
        <h3>Sample — 5 representative facts (with labels where available)</h3>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Local name</th>
              <th>Namespace</th>
              <th>Label (EN)</th>
              <th>Value</th>
              <th>Context</th>
              <th>Unit</th>
              <th>Decimals</th>
            </tr>
          </thead>
          <tbody>
            @for (f of lightLoadSampleFacts(); track $index) {
              <tr>
                <td>{{ $index + 1 }}</td>
                <td><code>{{ f.localName }}</code></td>
                <td><code>{{ f.namespace }}</code></td>
                <td>
                  @if (f.label) {
                    {{ f.label }}
                  } @else {
                    <em>(no label found)</em>
                  }
                </td>
                <td><pre>{{ f.value }}</pre></td>
                <td><code>{{ f.contextRef }}</code></td>
                <td>
                  @if (f.unitRef) { <code>{{ f.unitRef }}</code> } @else { — }
                </td>
                <td>{{ f.decimals || '—' }}</td>
              </tr>
            }
          </tbody>
        </table>
      }
    }
  </section>

  <section>
    <h2>Reports — Excel export (ATL + DPM + XBRL facts)</h2>
    <p>
      Builds a multi-sheet <code>.xlsx</code> (Contents + filed tables). Review the
      tabular preview and signature stats below, then download when it looks right.
      Does not run validation.
    </p>
    <p>
      <app-job-status-line mode="inline" [phase]="reportsStatus()" />
      @if (reportsFilename()) {
        — file: <code>{{ reportsFilename() }}</code>
      }
    </p>

    @if (reportsStatus() === 'fetch' ||
         reportsStatus() === 'write' ||
         reportsStatus() === 'parse' ||
         reportsStatus() === 'indicators' ||
         reportsStatus() === 'render' ||
         reportsStatus() === 'write-xlsx') {
      <p>
        <app-job-status-line mode="inline" prefix="Phase" [phase]="reportsStatus()" />
        — elapsed: {{ reportsElapsedMs() }} ms
      </p>
    }

    @if (reportsError()) {
      <dl>
        <dt>Error ({{ reportsErrorPhase() }})</dt>
        <dd><code>{{ reportsError() }}</code></dd>
      </dl>
    }

    @if (reportsStatus() === 'ready') {
      <dl>
        <dt>Download filename</dt>
        <dd>
          <code>{{ reportsDownloadFilename() }}</code>
          <button type="button" [disabled]="!reportsXlsxReady()" (click)="downloadReports()">
            Download Excel
          </button>
        </dd>
        <dt>Tables exported</dt>
        <dd>{{ reportsTableCount() }}</dd>
        <dt>Positive filing indicators</dt>
        <dd>{{ reportsFiledIndicatorCount() }}</dd>
        <dt>ATL metric slots (preview rows)</dt>
        <dd>
          {{ reportsDebugFilledCount() }} filled /
          {{ reportsDebugSlotCount() }} total
        </dd>
        <dt>fetch / write / load / export</dt>
        <dd>
          {{ reportsFetchMs() }} /
          {{ reportsWriteMs() }} /
          {{ reportsLoadMs() }} /
          {{ reportsExportMs() }} ms
        </dd>
        <dt>Total</dt>
        <dd>{{ reportsTotalMs() }} ms</dd>
        <dt>WASM heap after load</dt>
        <dd>{{ formatBytesMB(reportsWasmHeapAfterLoadBytes()) }}</dd>
        <dt>WASM heap after export</dt>
        <dd>{{ formatBytesMB(reportsWasmHeapAfterExportBytes()) }}</dd>
      </dl>

      @if (reportsDebugStats(); as stats) {
        <h3>Signature matching (per table)</h3>
        <p>
          Entity: <code>{{ stats.entityLei }}</code> —
          period end: <code>{{ stats.periodEnd }}</code> —
          entry point: <code>{{ stats.entryPoint }}</code> —
          instance facts: {{ stats.instanceFactCount }}
        </p>
        <table class="sample-table">
          <thead>
            <tr>
              <th>Template</th>
              <th>Table</th>
              <th>ATL sheet</th>
              <th>Slots</th>
              <th>Filled</th>
              <th>Facts checked</th>
              <th>With dim sig</th>
              <th>Matched</th>
              <th>No sig match</th>
              <th>Ambig. metric</th>
              <th>DPM sig. keys</th>
              @if (reportsStatsHasOpenTables(stats)) {
                <th>Rows mat.</th>
                <th>Table metrics</th>
              }
            </tr>
          </thead>
          <tbody>
            @for (t of stats.tables; track t.table + t.template) {
              <tr>
                <td><code>{{ t.template }}</code></td>
                <td><code>{{ t.table }}</code></td>
                <td>{{ t.atlSheet }}</td>
                <td>{{ t.atlSlots }}</td>
                <td>{{ t.valuesFilled }}</td>
                <td>{{ t.signatureStats.factsChecked }}</td>
                <td>{{ t.signatureStats.factsWithDimSignature }}</td>
                <td>{{ t.signatureStats.factsMatchedToTable }}</td>
                <td>{{ t.signatureStats.factsNoTableSignature }}</td>
                <td>{{ t.signatureStats.factsAmbiguousMetric }}</td>
                <td>{{ t.signatureStats.dpmIndexSize }}</td>
                @if (reportsStatsHasOpenTables(stats)) {
                  <td>
                    @if (t.openTable) {
                      {{ t.signatureStats.rowsMaterialized ?? 0 }}
                    } @else {
                      —
                    }
                  </td>
                  <td>
                    @if (t.openTable) {
                      {{ t.signatureStats.factsTableMetric ?? 0 }}
                    } @else {
                      —
                    }
                  </td>
                }
              </tr>
            }
          </tbody>
        </table>
        <p class="reports-debug-hint">
          Export omvat alle tabellen met een <strong>positieve filing indicator</strong> in het
          instance-bestand (niet elke ATL-sheet in het werkboek). <strong>DPM sig. keys</strong> =
          signature-indexgrootte.           <strong>No sig match</strong> = bij vaste tabellen: feiten zonder passende DPM-handtekening;
          bij open tabellen: feiten met een tabel-metric maar verkeerde dimensie-handtekening (niet alle 80 instance-facts).
          <strong>Ambig. metric</strong> = handtekening ok maar metric onduidelijk.
          <strong>Slots</strong> = vaste tabellen: ATL-cellen met VariableID; open tabellen: DPM-kolomdefinities.
          <strong>Filled</strong> = gematchte waarden; open-tabellen-preview alleen gematerialiseerde rijen.
          Open-tabellen RCA: browserconsole <code>[reports] open-table diagnostics</code> na export.
        </p>
      }

      @if (reportsDebugRows().length) {
        <h3>Report data preview (before download)</h3>
        <table class="sample-table reports-debug-table">
          <thead>
            <tr>
              <th>Entity / LEI</th>
              <th>Reporting period end</th>
              <th>Entry point / framework</th>
              <th>Template</th>
              <th>Table</th>
              <th>Columnnr</th>
              <th>Rownr</th>
              <th>Value</th>
              <th>Data type</th>
              <th>Unit</th>
              <th>Decimals</th>
              <th>VariableID</th>
            </tr>
          </thead>
          <tbody>
            @for (row of reportsDebugRows(); track row.variableId + row.table + row.columnNr + row.rowNr) {
              <tr [class.reports-debug-filled]="row.value !== null && formatReportsDebugValue(row.value) !== ''">
                <td>{{ row.entityLei }}</td>
                <td>{{ row.periodEnd }}</td>
                <td>{{ row.entryPoint }}</td>
                <td><code>{{ row.template }}</code></td>
                <td><code>{{ row.table }}</code></td>
                <td>{{ row.columnNr }}</td>
                <td>{{ row.rowNr }}</td>
                <td>{{ formatReportsDebugValue(row.value) }}</td>
                <td>{{ row.dataType || row.valueKind }}</td>
                <td>{{ row.unit }}</td>
                <td>{{ row.decimals }}</td>
                <td><code>{{ row.variableId }}</code></td>
              </tr>
            }
          </tbody>
        </table>
      } @else if (reportsStatus() === 'ready') {
        <p>
          Geen ATL metric-slots gevonden voor de preview (layout/VariableID in template).
          Controleer signature stats hierboven.
        </p>
      }
    }
  </section>

  <section>
    <h2>Trending test — multiple filings retained simultaneously</h2>
    <p>
      Parses the same filing <strong>{{ trendingTestCopies() ?? 2 }} times</strong> in sequence,
      keeping every parsed copy in Python memory at the same time. Confirms that
      the Fase 1 trending-feasibility budget (≤ 2 GiB for 2 filings of the same
      framework) is met. Same lxml-only path as Light load — no Arelle.
    </p>
    <p>
      <app-job-status-line mode="inline" [phase]="trendingTestStatus()" />
      @if (trendingTestFilename()) {
        — file: <code>{{ trendingTestFilename() }}</code>
      }
      @if (trendingTestStatus() === 'parse' && trendingTestCopyIndex() !== null) {
        — parsing copy <strong>{{ (trendingTestCopyIndex() ?? 0) + 1 }}</strong>
      }
    </p>

    @if (trendingTestStatus() === 'fetch' ||
         trendingTestStatus() === 'write' ||
         trendingTestStatus() === 'parse' ||
         trendingTestStatus() === 'labels') {
      <p>
        <app-job-status-line mode="inline" prefix="Phase" [phase]="trendingTestStatus()" />
        — elapsed: {{ trendingTestElapsedMs() }} ms
      </p>
    }

    @if (trendingTestError()) {
      <dl>
        <dt>Error ({{ trendingTestErrorPhase() }})</dt>
        <dd><code>{{ trendingTestError() }}</code></dd>
      </dl>
    }

    @if (trendingTestStatus() === 'ready' && trendingTestCopies() !== null) {
      <dl>
        <dt>Bytes (instance file)</dt>
        <dd>{{ formatBytesMB(trendingTestBytes()) }} ({{ trendingTestBytes() }} B)</dd>
        <dt>Copies parsed</dt>
        <dd>{{ trendingTestCopies() }}</dd>
        <dt>fetch (once)</dt>
        <dd>{{ trendingTestFetchMs() }} ms</dd>
        <dt>write (once)</dt>
        <dd>{{ trendingTestWriteMs() }} ms</dd>
        <dt>label index build</dt>
        <dd>
          @if (trendingTestLabelsBuilt()) {
            {{ trendingTestLabelBuildMs() }} ms (built this run)
          } @else {
            0 ms (reused cached index)
          }
        </dd>
        <dt>Trending test total</dt>
        <dd><strong>{{ trendingTestTotalMs() }} ms</strong></dd>

        <dt>Heap before (baseline)</dt>
        <dd>{{ formatBytesMB(trendingTestHeapBeforeBytes()) }}</dd>
        <dt>Heap after labels</dt>
        <dd>
          <strong>{{ formatBytesMB(trendingTestHeapAfterLabelsBytes()) }}</strong>
        </dd>

        <dt>Unique concepts (per copy, identical)</dt>
        <dd>{{ trendingTestUniqueConceptsCount() }}</dd>
        <dt>Label coverage</dt>
        <dd>
          {{ trendingTestUniqueWithLabel() }} matched,
          {{ trendingTestUniqueWithoutLabel() }} missing
          @if (trendingTestUniqueConceptsCount() && trendingTestUniqueConceptsCount()! > 0) {
            ({{ ((trendingTestUniqueWithLabel() ?? 0) / trendingTestUniqueConceptsCount()! * 100).toFixed(1) }}%)
          }
        </dd>
        <dt>Label index size</dt>
        <dd>{{ trendingTestLabelIndexSize() }} concept→label entries</dd>
      </dl>

      @if (trendingTestParseMsPerCopy().length > 0) {
        <h3>Per-copy timings &amp; heap growth</h3>
        <table>
          <thead>
            <tr>
              <th>Copy #</th>
              <th>Parse (ms)</th>
              <th>Facts</th>
              <th>Contexts</th>
              <th>Units</th>
              <th>Heap after parse</th>
              <th>Δ heap (this copy)</th>
            </tr>
          </thead>
          <tbody>
            @for (ms of trendingTestParseMsPerCopy(); track $index) {
              <tr>
                <td>{{ $index + 1 }}</td>
                <td>{{ ms }}</td>
                <td>{{ trendingTestFactsPerCopy()[$index] }}</td>
                <td>{{ trendingTestContextsPerCopy()[$index] }}</td>
                <td>{{ trendingTestUnitsPerCopy()[$index] }}</td>
                <td>{{ formatBytesMB(trendingTestHeapAfterParsePerCopy()[$index]) }}</td>
                <td>{{ (trendingTestHeapDeltaPerCopy()[$index] / (1024 * 1024)).toFixed(1) }} MiB</td>
              </tr>
            }
          </tbody>
        </table>
      }
    }
  </section>
</main>
